/* =============================================================
   NATIVE BEHAVIORS — the things that betray a non-native app
   Drop-in helpers for Capacitor + WKWebView.
   ============================================================= */

import { Haptics, ImpactStyle, NotificationType } from "@capacitor/haptics";
import { Capacitor } from "@capacitor/core";

/* ---------- PLATFORM MARKER ----------
   Tague <html> pour que le CSS s'adapte à la coquille :
   - `is-native`  : tourne dans une coquille native (Capacitor iOS/Catalyst OU
     la coquille AppKit Mac, qui n'embarque PAS Capacitor — elle se signale par
     le token `AgencyOSMacShell` dans l'User-Agent).
   - `is-mac`     : app Mac (Catalyst ou coquille AppKit) → la barre haute
     réserve la place des feux tricolores intégrés à son niveau. L'iPhone est
     natif mais son UA n'a pas "Macintosh".
   À appeler une fois à l'init (avant le premier rendu de préférence).         */
export function markPlatform() {
  const html = document.documentElement;
  const macShell = /AgencyOSMacShell/.test(navigator.userAgent);
  const native = Capacitor.isNativePlatform() || macShell;
  html.classList.toggle("is-native", native);
  html.classList.toggle("is-mac", macShell || (native && /Macintosh/.test(navigator.userAgent)));
}

/* ---------- HAPTICS ----------
   Call on taps, toggles, success/error. Biggest "native feel" win.
   Requires: npm i @capacitor/haptics  +  npx cap sync             */
export const haptic = {
  light: () => Haptics.impact({ style: ImpactStyle.Light }),
  medium: () => Haptics.impact({ style: ImpactStyle.Medium }),
  heavy: () => Haptics.impact({ style: ImpactStyle.Heavy }),
  select: () => Haptics.selectionStart(),
  success: () => Haptics.notification({ type: NotificationType.Success }),
  warning: () => Haptics.notification({ type: NotificationType.Warning }),
  error: () => Haptics.notification({ type: NotificationType.Error }),
};

/* Example React usage:
   <button className="btn btn-filled" onClick={() => { haptic.light(); save(); }}>
     Save
   </button>
*/

/* ---------- LARGE TITLE COLLAPSE ----------
   iOS large title shrinks into the nav bar as you scroll.
   Attach to a scroll container; toggles a class at threshold.   */
export function attachLargeTitleCollapse(
  scrollEl: HTMLElement,
  navbarEl: HTMLElement,
  threshold = 52,
) {
  const onScroll = () => {
    navbarEl.classList.toggle("collapsed", scrollEl.scrollTop > threshold);
  };
  scrollEl.addEventListener("scroll", onScroll, { passive: true });
  return () => scrollEl.removeEventListener("scroll", onScroll);
}
/* Pair with CSS:
   .navbar-large-title { transition: opacity .2s, transform .2s; }
   .navbar.collapsed .navbar-large-title { opacity: 0; transform: translateY(-8px); }
   .navbar .title { opacity: 0; transition: opacity .2s; }
   .navbar.collapsed .title { opacity: 1; }
*/

/* ---------- MOMENTUM SCROLL ----------
   Apply to any scroll area so it scrolls like iOS, not like a webpage. */
export function enableMomentumScroll(el: HTMLElement) {
  el.style.overflowY = "auto";
  (el.style as any).webkitOverflowScrolling = "touch";
  (el.style as any).overscrollBehavior = "contain";
}

/* ---------- DISABLE WEB GESTURES THAT LEAK ----------
   Kills long-press text callout, double-tap zoom, pull-to-refresh bounce
   on the document. Call once at app init.                       */
export function lockWebChrome() {
  const s = document.documentElement.style;
  (s as any).webkitUserSelect = "none";
  (s as any).webkitTouchCallout = "none";
  (s as any).overscrollBehavior = "none";
  // Allow selection inside inputs/textareas only
  const style = document.createElement("style");
  style.textContent =
    "input, textarea, [contenteditable] { -webkit-user-select: text; user-select: text; }";
  document.head.appendChild(style);
}
